<template>
  <div
    class="flex relative rounded-2xl border border-white focus:border-primary border-b-[#f0f1ff] transition-all duration-300"
    :class="{ '!border-primary border-b-primary': isFocus }"
  >
    <div class="relative">
      <span class="absolute top-1/2 -translate-y-1/2 left-3 text-txsecondary text-sm">
        {{ fromLabel }}
      </span>
      <input
        v-model="fromValue"
        @input="handleFromInput"
        @keydown="handleKeydown"
        @paste="handlePaste"
        @focus="isFocus = true"
        @blur="isFocus = false"
        type="text"
        :placeholder="fromPlaceholder"
        :style="{ paddingLeft: fromPadding || '2.2rem' }"
        class="w-full px-3 py-3.5 rounded-l-[15px] text-sm border border-b-2 border-r-0 border-white placeholder:text-[#959B9F] flex items-center leading-3"
      />
    </div>
    <div class="relative">
      <span class="absolute top-1/2 -translate-y-1/2 left-3 text-txsecondary text-sm">
        {{ toLabel }}
      </span>
      <input
        v-model="toValue"
        @input="handleToInput"
        @keydown="handleKeydown"
        @paste="handlePaste"
        @focus="isFocus = true"
        @blur="isFocus = false"
        type="text"
        :placeholder="toPlaceholder"
        :style="{ paddingLeft: toPadding || '2.2rem' }"
        class="w-full px-3 py-3.5 rounded-r-[15px] text-sm border border-b-2 border-white placeholder:text-[#959B9F] flex items-center leading-3"
      />
    </div>
    <el-slider
      v-model="rangeValue"
      :range="isRange"
      class="absolute -bottom-[1px] px-3 runway-none"
      tooltip-class="ui-tooltip"
      :min="0"
      :max="100"
      :format-tooltip="(value) => positionToValue(value).toString()"
      @update:model-value="handleRangeChange"
    />
  </div>
</template>

<script setup lang="ts">
  import { onMounted } from 'vue';
  interface Props {
    min?: number;
    max?: number;
    fromLabel?: string;
    toLabel?: string;
    fromPlaceholder?: string;
    toPlaceholder?: string;
    fromPadding?: string;
    toPadding?: string;
    isRange?: boolean;
  }

  const props = withDefaults(defineProps<Props>(), {
    min: 0,
    max: 100,
    fromLabel: "От",
    toLabel: "До",
    fromPlaceholder: "0",
    toPlaceholder: "0",
    isRange: true,
  });

  const emit = defineEmits(["update:modelValue"]);

  const modelValue = defineModel<number[]>({ required: true });

  const fromValue = ref<number>(props.min);
  const toValue = ref<number>(props.max);
  const rangeValue = ref<number[]>([0, 100]);

  const isFocus = ref(false);

  const createPriceScale = (maxValue: number) => {
    // Кастомная шкала для цен
    const baseScale = [
      50, 100, 150, 200, 300, 400, 500, 600, 700, 800, 900, 1000,
      1500, 2000, 3000, 4000, 5000, 6000, 7000, 8000, 9000, 10000,
      15000, 20000, 30000, 40000, 50000, 60000, 70000, 80000, 90000, 100000,
      150000, 200000, 250000
    ];
    
    // Добавляем шаги по 50к после 250000
    let currentValue = 250000;
    const scale = [...baseScale];
    
    while (currentValue < maxValue) {
      currentValue += 50000;
      scale.push(currentValue);
    }
    
    // Если максимальное значение больше последнего в шкале, добавляем его
    if (maxValue > scale[scale.length - 1]) {
      scale.push(maxValue);
    }
    
    return scale.filter(value => value <= maxValue);
  };

  const priceScale = computed(() => createPriceScale(props.max));

  const positionToValue = (position: number): number => {
    if (position <= 0) return props.min;
    if (position >= 100) return priceScale.value[priceScale.value.length - 1];

    // Преобразуем позицию (0-100) в индекс массива
    const scaleIndex = (position / 100) * (priceScale.value.length - 1);
    const nearestIndex = Math.round(scaleIndex);
    
    // Возвращаем ближайшее значение из шкалы без интерполяции
    return priceScale.value[Math.min(nearestIndex, priceScale.value.length - 1)];
  };

  const valueToPosition = (value: number): number => {
    if (value <= props.min) return 0;
    if (value >= priceScale.value[priceScale.value.length - 1]) return 100;

    // Находим точное совпадение или ближайшее значение в шкале
    let closestIndex = 0;
    let minDiff = Math.abs(value - priceScale.value[0]);
    
    for (let i = 1; i < priceScale.value.length; i++) {
      const diff = Math.abs(value - priceScale.value[i]);
      if (diff < minDiff) {
        minDiff = diff;
        closestIndex = i;
      }
    }
    
    // Преобразуем индекс в позицию (0-100)
    return (closestIndex / (priceScale.value.length - 1)) * 100;
  };

  const handleFromInput = (e: Event) => {
    const target = e.target as HTMLInputElement;
    const value = target.value.replace(/\D/g, "");
    const numValue = value ? parseInt(value) : 0;
    fromValue.value = Math.min(Math.max(numValue, props.min), toValue.value);

    rangeValue.value = [valueToPosition(fromValue.value), valueToPosition(toValue.value)];
    emit("update:modelValue", [fromValue.value, toValue.value]);
  };

  const handleToInput = (e: Event) => {
    const target = e.target as HTMLInputElement;
    const value = target.value.replace(/\D/g, "");
    const numValue = value ? parseInt(value) : 0;
    const maxPriceValue = priceScale.value[priceScale.value.length - 1];
    toValue.value = Math.min(Math.max(numValue, fromValue.value), maxPriceValue);

    rangeValue.value = [valueToPosition(fromValue.value), valueToPosition(toValue.value)];
    emit("update:modelValue", [fromValue.value, toValue.value]);
  };

  const handleRangeChange = (value: number | number[]) => {
    if (Array.isArray(value)) {
      fromValue.value = positionToValue(value[0]);
      toValue.value = positionToValue(value[1]);
      rangeValue.value = value;
      emit("update:modelValue", [fromValue.value, toValue.value]);
    }
  };

  const handleKeydown = (e: KeyboardEvent) => {
    const allowedKeys = [
      "Backspace",
      "Delete",
      "Tab",
      "Escape",
      "Enter",
      "Home",
      "End",
      "ArrowLeft",
      "ArrowRight",
      "ArrowUp",
      "ArrowDown",
    ];

    if (e.ctrlKey || e.metaKey) {
      const allowedCtrlKeys = ["a", "c", "v", "x", "z"];
      if (allowedCtrlKeys.includes(e.key.toLowerCase())) {
        return;
      }
    }

    if (/^[0-9]$/.test(e.key)) {
      return;
    }

    if (allowedKeys.includes(e.key)) {
      return;
    }

    e.preventDefault();
  };

  const handlePaste = (e: ClipboardEvent) => {
    e.preventDefault();
    const paste = e.clipboardData?.getData("text") || "";
    const numbersOnly = paste.replace(/\D/g, "");

    if (numbersOnly) {
      const target = e.target as HTMLInputElement;
      const currentValue = target.value;
      const selectionStart = target.selectionStart || 0;
      const selectionEnd = target.selectionEnd || 0;

      const newValue =
        currentValue.substring(0, selectionStart) +
        numbersOnly +
        currentValue.substring(selectionEnd);

      target.value = newValue;
      target.dispatchEvent(new Event("input", { bubbles: true }));
    }
  };

  watch(
    () => modelValue.value,
    (newValue) => {
      if (newValue && newValue.length === 2) {
        fromValue.value = newValue[0];
        toValue.value = newValue[1];
        rangeValue.value = [valueToPosition(newValue[0]), valueToPosition(newValue[1])];
      }
    },
    { immediate: true }
  );

  // Инициализируем значения при создании компонента
  onMounted(() => {
    if (priceScale.value.length > 0) {
      const maxPriceValue = priceScale.value[priceScale.value.length - 1];
      fromValue.value = props.min;
      toValue.value = maxPriceValue;
      rangeValue.value = [0, 100];
      emit("update:modelValue", [props.min, maxPriceValue]);
    }
  });

  // Следим за изменениями priceScale и обновляем значения
  watch(
    () => priceScale.value,
    (newScale) => {
      if (newScale.length > 0 && toValue.value === props.max) {
        const maxPriceValue = newScale[newScale.length - 1];
        toValue.value = maxPriceValue;
        rangeValue.value = [valueToPosition(fromValue.value), valueToPosition(maxPriceValue)];
        emit("update:modelValue", [fromValue.value, maxPriceValue]);
      }
    },
    { immediate: true }
  );
</script>

<style lang="scss" scoped>
  input {
    color: theme("colors.txmain");
    font-size: 14px;
    height: auto;
    background-color: theme("colors.bgmain");
    border: 0 !important;

    &:focus {
      --tw-ring-offset-shadow: var(--tw-ring-inset) 0 0 0 var(--tw-ring-offset-width)
        var(--tw-ring-offset-color);
      --tw-ring-shadow: var(--tw-ring-inset) 0 0 0 calc(0px + var(--tw-ring-offset-width))
        var(--tw-ring-color);
      box-shadow: var(--tw-ring-offset-shadow), var(--tw-ring-shadow), var(--tw-shadow, 0 0 #0000);
      --tw-ring-color: theme("colors.brfocus");
    }

    &::placeholder {
      color: theme("colors.txopacity");
    }
  }

  :deep(.el-slider) {
    .el-slider__runway {
      height: 2px;
      background-color: #f0f1ff;
    }

    .el-slider__bar {
      background-color: theme("colors.primary");
    }

    .el-slider__button {
      width: 14px;
      height: 14px;
      border: 2px solid white;
      background-color: theme("colors.primary");
      filter: drop-shadow(0px 1px 2.3px rgba(0, 0, 0, 0.12));
    }
  }
</style>

<style lang="scss">
  .ui-tooltip {
    background-color: white !important;
    color: theme("colors.txmain") !important;
    border: 0 !important;
    border-radius: 6px !important;
    box-shadow: 1px 2px 3px rgba(0, 0, 0, 0.12) !important;

    .el-popper__arrow {
      display: none;
    }
  }
</style>
